import ControlledTable from "@/components/controlled-table";
import { useTableServer } from "@/hooks/useTableServer";
import UserRepository from "@/utilities/repositories/Users";
import { useCallback, useEffect, useMemo } from "react";
import { getColumns } from "./columns";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { IUserDataResponse } from "@/utilities/types/Users";
import FilterElement from "./filter-element";
import { TUserSchemaInput } from "@/utilities/validators/user";
import useProfile from "@/hooks/use-profile";
import RoundRobinRepository from "@/utilities/repositories/RoundRobin";
import { UpdateResult } from "@/utilities/types/RoundRobin";
import { IQueueState } from "@/utilities/types/RoundRobin";
import { Text } from "rizzui";

export default function TeamTable() {
  const {
    loading: isQueueLoading,
    response: queueResponse,
    error: queueError,
    request: getQueue,
  } = useApi<IResponseData<IQueueState>>({
    request: RoundRobinRepository.getQueue,
  });

  useEffect(() => {
    void getQueue().catch(() => undefined);
  }, [getQueue]);

  const { request: deleteUser } = useApi<IResponseData<IUserDataResponse>>({
    request: UserRepository.deleteUser,
    enableToast: true,
  });

  const { request: deleteUserSubAccount } = useApi<IResponseData<UpdateResult>>(
    {
      request: RoundRobinRepository.deleteUserSubAccount,
      enableToast: true,
    }
  );

  const { request: setPassword } = useApi<IResponseData<TUserSchemaInput>>({
    request: UserRepository.setPassword,
    enableToast: true,
  });

  const onHeaderCellClick = (value: string) => ({
    onClick: () => {
      handleSort(value);
    },
  });

  const { profile } = useProfile();

  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    searchTerm,
    handleSearch,
    sortConfig,
    handleSort,
    handleReset,
    refresh,
  } = useTableServer({
    server: {
      request: UserRepository.getTeamUsers,
      key: "get-users-team-member",
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
  });

  const onSetPassword = (id: string, password: string) => {
    setPassword({ userId: id, password });
    refresh();
  };

  const onDeleteItem = useCallback(
    async (id: string, type?: "ownerUser" | "subUser") => {
      if (type === "ownerUser") {
        await deleteUser(id);
      } else {
        await deleteUserSubAccount({
          data: {
            subUserId: id,
          },
        });
      }

      await getQueue().catch(() => undefined);
      handleReset(); // Recall API
    },
    [deleteUser, getQueue, handleReset, deleteUserSubAccount]
  );

  const columns = useMemo(
    () =>
      getColumns({
        data: tableData,
        sortConfig,
        onHeaderCellClick,
        onDeleteItem,
        profile,
        onSetPassword,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onHeaderCellClick,
      sortConfig.key,
      sortConfig.direction,
      onDeleteItem,
      tableData,
      profile,
      onSetPassword,
    ]
  );

  const queue = queueResponse?.data;
  const nextMember = useMemo(
    () => queue?.queue.find((member) => member.isNext),
    [queue]
  );
  const getMemberName = (member: IQueueState["queue"][number]) =>
    [member.firstName, member.lastName].filter(Boolean).join(" ") ||
    member.email ||
    member.userId;

  return (
    <div className="mt-4">
      <FilterElement
        onSearch={handleSearch}
        searchTerm={searchTerm}
        variant="users"
      />
      <div className="mb-4 rounded-md border border-muted bg-gray-50 p-4 shadow-sm">
        <Text className="font-semibold">Lead Queue</Text>
        {isQueueLoading && (
          <Text className="text-sm text-gray-500">Loading queue...</Text>
        )}
        {queueError && (
          <Text className="text-sm text-red-500">
            Unable to load the round-robin queue.
          </Text>
        )}
        {!isQueueLoading && !queueError && queue?.fallback && (
          <Text className="text-sm text-gray-500">
            Round-robin is not active. Leads go to the main account.
          </Text>
        )}
        {!isQueueLoading &&
          !queueError &&
          queue?.queue.length === 0 &&
          !queue.fallback && (
          <Text className="text-sm text-gray-500">
            No active queue members.
          </Text>
        )}
        {!isQueueLoading && !queueError && queue && queue.queue.length > 0 && (
          <div className="mt-2 space-y-2">
            <Text className="text-sm">
              Next in queue: <strong>{nextMember && getMemberName(nextMember)}</strong>
            </Text>
            <div className="flex flex-wrap gap-2">
              {queue.queue.map((member) => (
                <div key={member.userId} className="text-sm text-gray-600">
                  <span className="mr-1 font-medium">#{member.position}</span>
                  {getMemberName(member)}
                  {member.isNext && " (next)"}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        // @ts-expect-error ControlledTable's generic columns are narrower than the table's runtime columns.
        columns={columns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </div>
  );
}
